import { Button } from "primereact/button";
import { useDispatch, useSelector } from "react-redux";
import ContractPopup from "src/components/AssignBuilding/ContractPopup";

import {
  setIsNewMode,
  setSelectedContract,
  setIsContractPopupOpened,
  setBuildingInfo,
  setOriginalAppointments,
  setContractInfo,
  resetContractInfo,
  setBMCBuildingInfo,
  setAssignedBuildingDetails,
} from "src/redux/slices/assignBuildingSlice";
import { Dropdown } from "primereact/dropdown";
import { useTranslation } from "react-i18next";
import { IBmcBuildingInfo } from "src/redux/interfaces/IAssignBuilding";
import ModeEditOutlineOutlinedIcon from "@mui/icons-material/ModeEditOutlineOutlined";
import ControlPointIcon from "@mui/icons-material/ControlPoint";
import {
  formatDateV2,
  downloadFile,
  getFieldFieldValue,
  getTextValueById,
} from "src/utils/helpers";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import { isContractActive, isContractExpired } from "src/utils/common";
import VerticalDivider from "src/components/VerticalDivider/VerticalDivider";
import * as React from "react";
import useOnPageLeave from "src/hooks/useOnPageLeave";

const AssignedBuildingDetails = () => {
  const dispatch = useDispatch();

  const citiesOptions = useSelector((state: any) => state.lookup.citiesOptions);
  const emirateOptions = useSelector(
    (state: any) => state.lookup.emiratesOptions
  );
  const breadCrumbsPaths = useSelector(
    (state: any) => state.breadCrumb.breadCrumbItems
  );
  const bmcBuildingInfo = useSelector(
    (state: any) => state.assignBuilding.bmcBuildingInfo
  ) as IBmcBuildingInfo;

  const systems = useSelector((state: any) => state.lookup.systems);

  const { t } = useTranslation(["common", "assignBuilding"]);
  const civilDefenseContractApprovalStatuses = useSelector(
    (state: any) => state.lookup.civilDefenseContractApprovalStatuses
  );
  const contractTypes = useSelector((state: any) => state.lookup.contractTypes);
  const serviceTerritories = useSelector(
    (state: any) => state.lookup.allServiceTerritories
  );
  const contractStatuses = useSelector(
    (state: any) => state.lookup.contractStatuses
  );

  // function handleUnAssignBuilding(event: any): void {
  //   dispatch(unAssignBuilding() as any);
  // }

  const handleUpdateContract = () => {
    dispatch(setContractInfo(bmcBuildingInfo.selectedContract));
    dispatch(setBuildingInfo(bmcBuildingInfo));
    dispatch(
      setOriginalAppointments(bmcBuildingInfo.selectedContract?.preventiveDates)
    );
    dispatch(setIsNewMode(false));
    dispatch(setIsContractPopupOpened(true) as any);
  };

  const handleAddContract = () => {
    dispatch(resetContractInfo());
    dispatch(setIsNewMode(true));
    dispatch(setIsContractPopupOpened(true) as any);
  };

  function getCivilDefenseStatus(): string {
    const selectedCivilDefenseStatus =
      civilDefenseContractApprovalStatuses.find(
        (x: { id: number }) =>
          Number(x.id) ===
          bmcBuildingInfo.selectedContract?.civilDefenseContractApprovalStatusId
      );
    return getFieldFieldValue(selectedCivilDefenseStatus);
  }

  function getContractType(): string {
    const contractType = contractTypes.find(
      (x: { id: number }) =>
        Number(x.id) === bmcBuildingInfo.selectedContract?.contractTypeId
    );
    return getFieldFieldValue(contractType);
  }

  // function handleUnAssignSystem(event: any): void {
  //   dispatch(unAssignSystem() as any);
  // }

  const handleContractChange = (e: any): void => {
    const selectedContract = bmcBuildingInfo.contractDetails.find(
      (contract) => contract.contractId === e.value
    );
    dispatch(setSelectedContract(selectedContract));
    dispatch(setContractInfo(selectedContract));
    dispatch(setBuildingInfo(bmcBuildingInfo));
  };

  function getSystemName(id: any) {
    return getTextValueById(id, systems as unknown as [], "systemServiceId");
  }

  function getEmirate(id: any) {
    return getTextValueById(id, emirateOptions, "emirateId");
  }

  function getServiceTerritory(id: any) {
    return getTextValueById(id, serviceTerritories, "territoryId");
  }

  function getCity(id: any) {
    return getTextValueById(id, citiesOptions, "cityId");
  }

  const contractNumberTemplate = (option: any) => {
    if (!option) return null;
    const isActiveContract = isContractActive(option.statusId);
    return (
      <div className="d-flex align-items-center justify-content-between gap-2">
        <div className="overflow-hidden">{option.amcContractNumber}</div>
        {(() => {
          if (isActiveContract) {
            return <span className="oval green-oval">{t("active")}</span>;
          } else if (isContractExpired(option.statusCode, contractStatuses)) {
            return <span className="oval red-oval">{t("expired")} </span>;
          } else {
            return <span className="oval gray-oval">{t("unassigned")}</span>;
          }
        })()}
      </div>
    );
  };

  const getSystemIcon = (systems: any, systemServiceId: any) => {
    return systems.find((sys: any) => sys.systemServiceId === systemServiceId)
      ?.icon.fileBase64;
  };

  function renderPreventiveDates() {
    return bmcBuildingInfo.selectedContract?.preventiveDates?.map(
      (date: string, index: number) => (
        <span className="d-flex align-items-center gap-1" key={index + date}>
          <span className="plain-text-date">{formatDateV2(date)}</span>
          {index + 1 !==
            bmcBuildingInfo.selectedContract?.preventiveDates?.length && (
            <span> - </span>
          )}
        </span>
      )
    );
  }

  const resetPropertyDetails = () => {
    dispatch(setBMCBuildingInfo({}));
    dispatch(setAssignedBuildingDetails({}));
  };
  useOnPageLeave(resetPropertyDetails);

  return (
    <div className="maintenance-requests-details-popup assign-building-details-popup w-100">
      <div className="p-3">
        <div className="d-flex justify-content-between align-items-center mb-2 pb-2 border-bottom-1">
          {breadCrumbsPaths.length && (
            <h2 className="inner-page-title">
              {t(breadCrumbsPaths[breadCrumbsPaths.length - 1].label)}
            </h2>
          )}
        </div>

        <div className="d-flex justify-content-end align-items-center gap-2 my-2">
          <Button
            className="primary-btn edit-btn unassign-building-btn m-0"
            onClick={handleAddContract}
          >
            <ControlPointIcon fontSize="small" className="mx-2" />{" "}
            {t("addContract")}
          </Button>
        </div>
        <section>
          <h2 className="font-weight-700">{t("assignedPropertyDetails")}</h2>
          <div className="section-body">
            <div className="section-body__item w-32">
              <h4 className="font-weight-700">{t("propertyID")}</h4>
              <span>{bmcBuildingInfo.buildingInfo?.propertyNumber}</span>
            </div>
            <VerticalDivider />
            <div className="section-body__item ">
              <h4 className="font-weight-700">{t("buildingNameHeader")}</h4>
              <span>{bmcBuildingInfo.buildingInfo?.name}</span>
            </div>
          </div>
        </section>
        <section>
          <h2 className="font-weight-700">{t("addressInformation")}</h2>
          <div className="section-body">
            <div className="section-body__item w-32">
              <h4 className="font-weight-700">{t("latitude")}</h4>
              <span>{bmcBuildingInfo.buildingInfo?.address1_Latitude}</span>
            </div>
            <VerticalDivider />
            <div className="section-body__item w-32">
              <h4 className="font-weight-700">{t("longitude")}</h4>
              <span>{bmcBuildingInfo.buildingInfo?.address1_Longitude}</span>
            </div>
            <VerticalDivider />
            <div className="section-body__item">
              <h4 className="font-weight-700">{t("serviceTerritory")}</h4>
              <span>
                {getServiceTerritory(
                  bmcBuildingInfo.buildingInfo?.serviceTerritoryId
                )}
              </span>
            </div>
          </div>
          <div className="section-body">
            <div className="section-body__item w-32">
              <h4 className="font-weight-700">{t("address")}</h4>
              <span>{bmcBuildingInfo?.buildingInfo?.address1_Name}</span>
            </div>
            <VerticalDivider />
            <div className="section-body__item w-32">
              <h4 className="font-weight-700">{t("emirate")}</h4>
              <span>
                {getEmirate(bmcBuildingInfo?.buildingInfo?.emirateId)}
              </span>
            </div>
            <VerticalDivider />
            <div className="section-body__item">
              <h4 className="font-weight-700">{t("city")}</h4>
              <span>{getCity(bmcBuildingInfo?.buildingInfo?.cityId)}</span>
            </div>
          </div>
        </section>
        <section className="contract-info">
          <div className="contract-info__header">
            <h2 className="font-weight-700">{t("theContracts")}</h2>
            <div className="contract-info__header__actions">
              <span className="contract-info__header__actions__item">
                {t("contractNo")}
              </span>
              <Dropdown
                options={bmcBuildingInfo?.contractDetails}
                value={bmcBuildingInfo.selectedContract?.contractId}
                valueTemplate={contractNumberTemplate}
                placeholder={t("selectContract")}
                optionValue="contractId"
                optionLabel="amcContractNumber"
                itemTemplate={contractNumberTemplate}
                panelClassName="status-dropdown-panel"
                className="contract-dropdown"
                filter
                onChange={handleContractChange}
              />
              <Button
                className="light-primary-btn m-0 font-weight-600"
                onClick={handleUpdateContract}
                visible={
                  bmcBuildingInfo.selectedContract
                    ? isContractActive(
                        bmcBuildingInfo.selectedContract.statusId ?? null
                      )
                    : false
                }
              >
                <ModeEditOutlineOutlinedIcon fontSize="small" className="" />
                {t("editContract")}
              </Button>
            </div>
          </div>

          <div className="contract-info__body">
            {bmcBuildingInfo.selectedContract && (
              <div className="contract-info__body__details ">
                <h2 className="font-weight-700">{t("contractInformation")}</h2>
                <div className="contract-info__body__details__rows">
                  <div className="contract-info__body__details__rows__row border-bottom-1">
                    <div className="section-body__item ">
                      <h4 className="font-weight-700">{t("amcContractNo")}</h4>
                      <span>
                        {bmcBuildingInfo.selectedContract?.amcContractNumber ??
                          ""}
                      </span>
                    </div>
                    <VerticalDivider />
                    <div className="section-body__item ">
                      <h4 className="font-weight-700">{t("contractType")}</h4>
                      <span>{getContractType()}</span>
                    </div>
                    <VerticalDivider />
                    <div className="section-body__item ">
                      <h4 className="font-weight-700">{t("startDate")}</h4>
                      <span className="plain-text-date">
                        {formatDateV2(
                          bmcBuildingInfo.selectedContract?.startDate
                        ) ?? ""}
                      </span>
                    </div>
                    <VerticalDivider />
                    <div className="section-body__item">
                      <h4 className="font-weight-700">{t("endDate")}</h4>
                      <span className="plain-text-date">
                        {formatDateV2(
                          bmcBuildingInfo.selectedContract?.endDate
                        ) ?? ""}
                      </span>
                    </div>
                  </div>

                  <div className="contract-info__body__details__rows__row border-bottom-1">
                    <div className="section-body__item ">
                      <h4 className="font-weight-700">
                        {t("civilDefenseContractApprovalStatus")}
                      </h4>
                      <span className="oval blue-oval">
                        {getCivilDefenseStatus()}
                      </span>
                    </div>
                    <VerticalDivider />
                    <div className="section-body__item file-container">
                      <h4 className="font-weight-700"> {t("amcCopy")}</h4>
                      {bmcBuildingInfo.selectedContract?.amcCopy?.fileName ? (
                        <Button
                          className="m-0 p-0 me-2 download-btn"
                          text
                          onClick={() =>
                            downloadFile(
                              bmcBuildingInfo.selectedContract?.amcCopy,
                              t("fileSizeExceeds")
                            )
                          }
                        >
                          <FileDownloadIcon />{" "}
                        </Button>
                      ) : null}
                      <span className="file-name">
                        {bmcBuildingInfo.selectedContract?.amcCopy?.fileName ||
                          ""}
                      </span>
                    </div>
                    <VerticalDivider />
                    <div className="section-body__item w-50">
                      <h4 className="font-weight-700">
                        {" "}
                        {t("appointmentDates")}
                      </h4>
                      <div className="maintenance-dates">
                        {renderPreventiveDates()}
                      </div>
                    </div>
                  </div>

                  <div className="contract-info__body__details__rows__row border-bottom-1">
                    <div className="section-body__item ">
                      <h4 className="font-weight-700">
                        {t("engineerTechnicianName")}
                      </h4>
                      <span>
                        {bmcBuildingInfo.selectedContract
                          ?.engineerTechnicianName ?? ""}
                      </span>
                    </div>
                    <VerticalDivider />
                    <div className="section-body__item ">
                      <h4 className="font-weight-700">
                        {" "}
                        {t("technicianMobileNumber")}
                      </h4>
                      <span>
                        {bmcBuildingInfo.selectedContract
                          ?.technicianMobileNumber ?? ""}
                      </span>
                    </div>
                    <VerticalDivider />
                    <div className="section-body__item ">
                      <h4 className="font-weight-700">
                        {" "}
                        {t("technicianEmail")}
                      </h4>
                      <span>
                        {bmcBuildingInfo.selectedContract?.technicianEmail ??
                          ""}
                      </span>
                    </div>
                    <VerticalDivider />
                    <div className="section-body__item">
                      <h4 className="font-weight-700">
                        {t("technicianAlternateNumber")}
                      </h4>
                      <span>
                        {bmcBuildingInfo.selectedContract
                          ?.technicianAlternateNumber ?? ""}
                      </span>
                    </div>
                  </div>

                  <div className="contract-info__body__details__rows__row border-bottom-1">
                    <div className="section-body__item ">
                      <h4 className="font-weight-700">
                        {t("managerSupervisorName")}
                      </h4>
                      <span>
                        {bmcBuildingInfo.selectedContract
                          ?.managerSupervisorName ?? ""}
                      </span>
                    </div>
                    <VerticalDivider />
                    <div className="section-body__item ">
                      <h4 className="font-weight-700">
                        {" "}
                        {t("technicianMobileNumber")}
                      </h4>
                      <span>
                        {bmcBuildingInfo.selectedContract
                          ?.managerMobileNumber ?? ""}
                      </span>
                    </div>
                    <VerticalDivider />
                    <div className="section-body__item ">
                      <h4 className="font-weight-700">
                        {" "}
                        {t("technicianEmail")}
                      </h4>
                      <span>
                        {bmcBuildingInfo.selectedContract?.managerEmail ?? ""}
                      </span>
                    </div>
                    <VerticalDivider />
                    <div className="section-body__item">
                      <h4 className="font-weight-700">
                        {t("technicianAlternateNumber")}
                      </h4>
                      <span>
                        {bmcBuildingInfo.selectedContract
                          ?.managerAlternateNumber ?? ""}
                      </span>
                    </div>
                  </div>
                </div>
                <section>
                  <h2 className="font-weight-700">{t("systems")}</h2>
                  <div className="section-body wide-body">
                    <div className="section-body__item systems-container w-100">
                      {bmcBuildingInfo.selectedContract.contractSystemServicesInfo?.map(
                        (
                          selected: {
                            quantity: any;
                            make: any;
                            model: any;
                            systemServiceId: any;
                          },
                          index: number
                        ) => (
                          <div
                            className="system-item d-flex pe-2 py-2 mt-2"
                            key={selected.systemServiceId}
                          >
                            <div className="d-flex justify-content-start align-items-center gap-1 w-40">
                              {
                                <img
                                  className="image-icon mx-3"
                                  src={`data:image/png;base64,${getSystemIcon(
                                    systems,
                                    selected.systemServiceId
                                  )}`}
                                  alt={selected.systemServiceId}
                                />
                              }
                              <h4>
                                {getSystemName(selected.systemServiceId) ?? ""}
                              </h4>
                            </div>
                            <div className="d-flex justify-content-between align-items-center gap-4 px-2 w-60">
                              <div>
                                <h4>{t("quantity")}</h4>
                                <span>{selected?.quantity}</span>
                              </div>
                              <div>
                                <h4>{t("make")}</h4>
                                <span>{selected?.make}</span>
                              </div>
                              <div className="system-model">
                                <h4>{t("model")}</h4>
                                <span>{selected?.model}</span>
                              </div>
                            </div>
                          </div>
                        )
                      )}
                    </div>
                  </div>
                </section>
              </div>
            )}
          </div>
        </section>
      </div>
      <ContractPopup />
    </div>
  );
};

export default AssignedBuildingDetails;
